import { useState, useMemo, useEffect } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { ChevronDown, History, Copy, Download, RotateCcw, Search } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import {
  Collapsible,
  CollapsibleContent,
  CollapsibleTrigger,
} from "@/components/ui/collapsible";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  AlertDialog,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { objectsApi } from "@/services/api-v2.service";
import type { ObjectCardAuditEntry, ObjectCardFieldChange } from "@/types/api-v2";
import type { ObjectCardResponse } from "@/types/card-template";
import { useToast } from "@/hooks/use-toast";
import { useAuth } from "@/hooks/useAuthV2";

const ACTION_LABEL: Record<string, string> = {
  card_attrs: "Карточка (атрибуты объекта)",
  card_restore: "Откат к версии из журнала",
};

const FILTER_OPTIONS = [
  { id: "all" as const, label: "Все" },
  { id: "card_attrs" as const, label: "Сохранение" },
  { id: "card_restore" as const, label: "Откаты" },
];

type ActionFilter = (typeof FILTER_OPTIONS)[number]["id"];

function formatDate(iso: string): string {
  try {
    return new Date(iso).toLocaleString("ru-RU", {
      dateStyle: "short",
      timeStyle: "short",
    });
  } catch {
    return iso;
  }
}

function actorLabel(row: ObjectCardAuditEntry): string {
  const u = row.user_username?.trim();
  if (u) return u;
  const e = row.user_email?.trim();
  if (e) return e;
  if (row.user_id) return `${row.user_id.slice(0, 10)}…`;
  return "система";
}

function parseFieldChanges(payload: Record<string, unknown>): ObjectCardFieldChange[] {
  const raw = payload.field_changes;
  if (!Array.isArray(raw)) return [];
  const out: ObjectCardFieldChange[] = [];
  for (const x of raw) {
    if (
      x &&
      typeof x === "object" &&
      "field_key" in x &&
      "old_value" in x &&
      "new_value" in x
    ) {
      const o = x as Record<string, unknown>;
      out.push({
        field_key: String(o.field_key),
        old_value: String(o.old_value),
        new_value: String(o.new_value),
      });
    }
  }
  return out;
}

function summaryLine(action: string, payload: Record<string, unknown>): string | null {
  const fc = parseFieldChanges(payload);
  if (fc.length > 0) {
    const trunc = payload.field_changes_truncated ? " (часть изменений обрезана)" : "";
    return `Изменено полей: ${fc.length}${trunc}`;
  }
  if (action === "card_restore") {
    const id = payload.restored_from_audit_id;
    if (typeof id === "number") {
      return `Восстановлено из записи журнала #${id}`;
    }
    return "Восстановление из журнала";
  }
  const fk = payload.field_keys;
  if (Array.isArray(fk) && fk.length > 0) {
    return `Поля в запросе: ${fk.map(String).join(", ")}`;
  }
  return null;
}

function hasRestorableSnapshot(payload: Record<string, unknown>): boolean {
  const s = payload.snapshot_object_values;
  return Array.isArray(s) && s.length > 0;
}

async function copyText(text: string): Promise<boolean> {
  try {
    await navigator.clipboard.writeText(text);
    return true;
  } catch {
    return false;
  }
}

/** Журнал изменений полей карточки на уровне объекта: diff, фильтры, откат. */
export function ObjectCardAuditCollapsible({ objectId }: { objectId: number }) {
  const [open, setOpen] = useState(false);
  const [actionFilter, setActionFilter] = useState<ActionFilter>("all");
  const [searchInput, setSearchInput] = useState("");
  const [debouncedSearch, setDebouncedSearch] = useState("");
  const [fieldKeyFilter, setFieldKeyFilter] = useState("");
  const [userIdFilter, setUserIdFilter] = useState<string>("");
  const [dateFrom, setDateFrom] = useState("");
  const [dateTo, setDateTo] = useState("");
  const [restoreTargetId, setRestoreTargetId] = useState<number | null>(null);
  const { toast } = useToast();
  const queryClient = useQueryClient();
  const { hasPermission } = useAuth();
  const canRestore = hasPermission("objects.card_audit.restore");

  useEffect(() => {
    const t = setTimeout(() => setDebouncedSearch(searchInput), 400);
    return () => clearTimeout(t);
  }, [searchInput]);

  const queryParams = useMemo(() => {
    const p: Parameters<typeof objectsApi.getObjectCardAudit>[1] = {
      limit: 200,
    };
    if (actionFilter !== "all") p.action = actionFilter;
    if (fieldKeyFilter.trim()) p.field_key = fieldKeyFilter.trim();
    if (debouncedSearch.trim()) p.search = debouncedSearch.trim();
    if (userIdFilter) p.user_id = userIdFilter;
    if (dateFrom) {
      const d = new Date(dateFrom);
      if (!Number.isNaN(d.getTime())) p.created_from = d.toISOString();
    }
    if (dateTo) {
      const d = new Date(dateTo);
      if (!Number.isNaN(d.getTime())) p.created_to = d.toISOString();
    }
    return p;
  }, [
    actionFilter,
    fieldKeyFilter,
    debouncedSearch,
    userIdFilter,
    dateFrom,
    dateTo,
  ]);

  const { data, isLoading, isError } = useQuery({
    queryKey: ["object-card-audit", objectId, queryParams],
    queryFn: () => objectsApi.getObjectCardAudit(objectId, queryParams),
    enabled: open,
  });

  const userOptions = useMemo(() => {
    const m = new Map<string, string>();
    for (const r of data ?? []) {
      const id = r.user_id;
      if (!id) continue;
      m.set(id, actorLabel(r));
    }
    return Array.from(m.entries()).sort((a, b) => a[1].localeCompare(b[1], "ru"));
  }, [data]);

  const restoreMutation = useMutation({
    mutationFn: (auditLogId: number) =>
      objectsApi.restoreObjectCardFromAudit(objectId, auditLogId),
    onSuccess: (cardData: ObjectCardResponse) => {
      queryClient.setQueryData<ObjectCardResponse>(
        ["object-card", objectId],
        cardData,
      );
      queryClient.invalidateQueries({ queryKey: ["object-card-audit", objectId] });
      queryClient.invalidateQueries({ queryKey: ["objects", objectId] });
      setRestoreTargetId(null);
      toast({
        title: "Версия восстановлена",
        description: "Атрибуты объекта обновлены по выбранной записи журнала.",
      });
    },
    onError: (err: unknown) => {
      const msg = err instanceof Error ? err.message : "Не удалось выполнить откат";
      toast({ title: "Ошибка отката", description: msg, variant: "destructive" });
    },
  });

  const exportAudit = () => {
    if (!data?.length) return;
    const blob = new Blob([JSON.stringify(data, null, 2)], {
      type: "application/json;charset=utf-8",
    });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `object-${objectId}-card-audit.json`;
    a.click();
    URL.revokeObjectURL(url);
    toast({ title: "Экспорт", description: "Файл журнала скачан" });
  };

  const rows = data ?? [];

  return (
    <>
      <AlertDialog
        open={restoreTargetId != null}
        onOpenChange={(v) => !v && setRestoreTargetId(null)}
      >
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Восстановить атрибуты объекта?</AlertDialogTitle>
            <AlertDialogDescription>
              Будет применён снимок из записи журнала #{restoreTargetId}. Текущие значения
              полей объекта будут заменены; геометрии и их атрибуты не изменятся.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={restoreMutation.isPending}>Отмена</AlertDialogCancel>
            <Button
              type="button"
              disabled={restoreMutation.isPending}
              onClick={() =>
                restoreTargetId != null && void restoreMutation.mutateAsync(restoreTargetId)
              }
            >
              {restoreMutation.isPending ? "Восстановление…" : "Восстановить"}
            </Button>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      <Collapsible open={open} onOpenChange={setOpen} className="mt-3">
        <CollapsibleTrigger asChild>
          <Button
            type="button"
            variant="ghost"
            size="sm"
            className="h-7 w-full justify-start gap-1.5 px-1 text-xs text-muted-foreground hover:text-foreground"
          >
            <History className="h-3 w-3 shrink-0" />
            <span>История изменений</span>
            <ChevronDown
              className={`h-3 w-3 shrink-0 transition-transform ${open ? "rotate-180" : ""}`}
            />
          </Button>
        </CollapsibleTrigger>
        <CollapsibleContent className="space-y-2 text-xs text-muted-foreground border-t border-dashed mt-1.5 pt-2">
          {isLoading && <p>Загрузка…</p>}
          {isError && <p className="text-destructive">Не удалось загрузить журнал</p>}
          {!isLoading && !isError && rows.length === 0 && (
            <p>
              Записей нет (или ничего не подошло под фильтры). История ведётся при сохранении
              карточки на вкладке «Карточка».
            </p>
          )}
          {!isLoading && open && (
            <div className="space-y-2 rounded-md border border-border/50 bg-muted/20 p-2 min-w-0">
              <div className="grid gap-2 grid-cols-1 sm:grid-cols-2 min-w-0">
                <div className="relative min-w-0">
                  <Search className="absolute left-2 top-1/2 h-3.5 w-3.5 -translate-y-1/2 text-muted-foreground pointer-events-none" />
                  <Input
                    className="h-8 pl-8 text-[11px] w-full min-w-0"
                    placeholder="Поиск по payload, email…"
                    value={searchInput}
                    onChange={(e) => setSearchInput(e.target.value)}
                  />
                </div>
                <Input
                  className="h-8 text-[11px] w-full min-w-0"
                  placeholder="Код поля (field_key)"
                  value={fieldKeyFilter}
                  onChange={(e) => setFieldKeyFilter(e.target.value)}
                />
              </div>
              <div className="min-w-0 w-full">
                <Select
                  value={userIdFilter || "__all__"}
                  onValueChange={(v) => setUserIdFilter(v === "__all__" ? "" : v)}
                >
                  <SelectTrigger className="h-8 text-[11px] w-full min-w-0 max-w-full">
                    <SelectValue placeholder="Пользователь" />
                  </SelectTrigger>
                  <SelectContent className="z-[250]">
                    <SelectItem value="__all__">Все пользователи</SelectItem>
                    {userOptions.map(([id, label]) => (
                      <SelectItem key={id} value={id}>
                        {label}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div className="flex flex-row gap-2 items-center min-w-0 w-full">
                <Input
                  className="h-8 text-[10px] min-w-0 flex-1 basis-0"
                  type="datetime-local"
                  value={dateFrom}
                  onChange={(e) => setDateFrom(e.target.value)}
                  title="С даты"
                  aria-label="С даты"
                />
                <Input
                  className="h-8 text-[10px] min-w-0 flex-1 basis-0"
                  type="datetime-local"
                  value={dateTo}
                  onChange={(e) => setDateTo(e.target.value)}
                  title="По дату"
                  aria-label="По дату"
                />
              </div>
              <div className="flex flex-wrap items-center gap-1">
                {FILTER_OPTIONS.map((opt) => (
                  <Button
                    key={opt.id}
                    type="button"
                    variant={actionFilter === opt.id ? "secondary" : "ghost"}
                    size="sm"
                    className="h-6 px-2 text-[10px]"
                    onClick={() => setActionFilter(opt.id)}
                  >
                    {opt.label}
                  </Button>
                ))}
                <Button
                  type="button"
                  variant="outline"
                  size="sm"
                  className="h-6 gap-1 px-2 text-[10px] ml-auto"
                  disabled={!rows.length}
                  onClick={exportAudit}
                  title="Скачать отфильтрованные записи как JSON"
                >
                  <Download className="h-3 w-3" />
                  JSON
                </Button>
              </div>
            </div>
          )}
          {!isLoading && rows.length > 0 && (
            <ul className="space-y-2 max-h-[28rem] overflow-y-auto pr-1">
              {rows.map((row) => {
                const extra = summaryLine(row.action, row.payload);
                const raw = JSON.stringify(row.payload ?? {}, null, 2);
                const changes = parseFieldChanges(row.payload);
                return (
                  <li
                    key={row.id}
                    className="rounded border border-border/60 bg-muted/30 px-2 py-1.5"
                  >
                    <div className="flex flex-wrap items-center justify-between gap-2">
                      <div className="flex flex-wrap items-baseline gap-x-2 gap-y-0.5 min-w-0">
                        <span className="font-medium text-foreground">
                          {ACTION_LABEL[row.action] ?? row.action}
                        </span>
                        <span className="text-[11px]">{formatDate(row.created_at)}</span>
                      </div>
                      {canRestore &&
                        hasRestorableSnapshot(row.payload) &&
                        row.action !== "card_restore" && (
                          <Button
                            type="button"
                            variant="outline"
                            size="sm"
                            className="h-6 shrink-0 gap-1 px-2 text-[10px]"
                            disabled={restoreMutation.isPending}
                            title="Восстановить атрибуты объекта к состоянию на момент этой записи"
                            onClick={() => setRestoreTargetId(row.id)}
                          >
                            <RotateCcw className="h-3 w-3" />
                            К этой версии
                          </Button>
                        )}
                    </div>
                    <div
                      className="text-[11px] mt-0.5 truncate"
                      title={row.user_id ?? undefined}
                    >
                      {actorLabel(row)}
                    </div>
                    {extra && (
                      <div className="text-[10px] mt-0.5 opacity-80 leading-tight">
                        {extra}
                      </div>
                    )}
                    {changes.length > 0 && (
                      <div className="mt-2 overflow-x-auto rounded border border-border/40 bg-background/50">
                        <table className="w-full text-[10px] text-left">
                          <thead>
                            <tr className="border-b border-border/50 text-muted-foreground">
                              <th className="p-1 font-medium">Поле</th>
                              <th className="p-1 font-medium">Было</th>
                              <th className="p-1 font-medium">Стало</th>
                            </tr>
                          </thead>
                          <tbody>
                            {changes.map((c) => (
                              <tr key={c.field_key} className="border-b border-border/30 align-top">
                                <td className="p-1 font-mono text-foreground">{c.field_key}</td>
                                <td className="p-1 whitespace-pre-wrap break-words max-w-[12rem]">
                                  {c.old_value}
                                </td>
                                <td className="p-1 whitespace-pre-wrap break-words max-w-[12rem]">
                                  {c.new_value}
                                </td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      </div>
                    )}
                    <details className="mt-1">
                      <summary className="cursor-pointer text-[10px] text-foreground/70 list-none flex items-center gap-1">
                        <span className="select-none">Технический payload (JSON)</span>
                      </summary>
                      <pre className="mt-1 max-h-24 overflow-auto rounded bg-background/80 p-1.5 text-[10px] leading-tight text-foreground/90 border border-border/50 whitespace-pre-wrap break-all">
                        {raw}
                      </pre>
                      <Button
                        type="button"
                        variant="ghost"
                        size="sm"
                        className="h-6 mt-1 gap-1 px-2 text-[10px]"
                        onClick={async () => {
                          const ok = await copyText(raw);
                          toast(
                            ok
                              ? { title: "Скопировано", description: "JSON payload в буфере" }
                              : {
                                  title: "Буфер",
                                  description: "Не удалось скопировать",
                                  variant: "destructive",
                                },
                          );
                        }}
                      >
                        <Copy className="h-3 w-3" />
                        Копировать JSON
                      </Button>
                    </details>
                  </li>
                );
              })}
            </ul>
          )}
        </CollapsibleContent>
      </Collapsible>
    </>
  );
}
